import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useSite } from '../context/SiteContext';
import ChangePasswordModal from './common/ChangePasswordModal';

const Sidebar = () => {
  const { user, logout, can } = useAuth();
  const { language, toggleLanguage, t } = useLanguage();
  const { currentSite, sites, canChangeSite, changeSite } = useSite();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  // Debug logging
  React.useEffect(() => {

  }, [user, can]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const menuSections = [
    {
      title: 'Control de Calidad',
      items: [
        {
          path: '/dashboard',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          ),
          label: t('nav.dashboard'),
          show: can('dashboard:view')
        },
        {
          path: '/capture',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          ),
          label: t('nav.capture'),
          show: can('capture:create')
        },
        {
          path: '/reporter',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          ),
          label: t('nav.reports'),
          show: can('reports:view')
        },
        {
          path: '/acciones',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          ),
          label: t('nav.acciones'),
          show: can('actions:manage')
        }
      ]
    },
    {
      title: 'Conversión',
      items: [
        {
          path: '/conversion',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-6a2 2 0 012-2h2a2 2 0 012 2v6m4 0H5m16 0a2 2 0 002-2V7a2 2 0 00-2-2H3a2 2 0 00-2 2v8a2 2 0 002 2m16 0v4m0 0H5m16 0a2 2 0 01-2 2H7a2 2 0 01-2-2v-4" />
            </svg>
          ),
          label: 'Reporte Diario',
          show: can('conversion:view')
        },
        {
          path: '/conversion/analytics',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v18h18M7 16l4-5 4 3 5-7" />
            </svg>
          ),
          label: 'Análisis de Producción',
          show: can('conversion:analytics')
        },
        {
          path: '/conversion/reportes-pdf',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 3h7l5 5v13H7a2 2 0 01-2-2V5a2 2 0 012-2z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M14 3v6h6M9 13h6M9 17h4" />
            </svg>
          ),
          label: 'Reportes PDF',
          show: can('conversion:reportespdf')
        },
        {
          path: '/conversion/reportes-programados',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 2v4m8-4v4M3 10h18M5 5h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2zm4 10h6m-3-3v6" />
            </svg>
          ),
          label: 'Reportes Programados',
          show: can('conversion:automatic_report_viewanduse') && Boolean(user?.email && user?.user_type !== 'employee' && !user?.employee_number)
        },
        {
          path: '/orders-data',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
          ),
          label: 'Orders Data',
          show: can('conversion:view') || can('conversion:ordersdata')
        },
        {
          path: '/employees',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          ),
          label: 'Empleados',
          show: can('conversion:employees')
        }
      ]
    },
    {
      title: 'Administración',
      items: [
        {
          path: '/catalogs',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          ),
          label: t('nav.catalogs'),
          show: can('catalogs:manage')
        },
        {
          path: '/sites',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          ),
          label: t('nav.sites'),
          show: can('sites:manage')
        },
        {
          path: '/users',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          ),
          label: t('nav.users'),
          show: can('users:manage')
        },
        {
          path: '/permissions',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-3-3v6m9 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          label: 'Permisos',
          show: can('permissions:view')
        }
      ]
    }
  ];

  if (!user) return null;

  return (
    <>
      {/* Sidebar */}
      <div className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
        {/* Header */}
        <div className="sidebar-header">
          <div className="flex items-center justify-between w-full">
            {!collapsed && (
              <Link to="/dashboard" className="sidebar-logo no-underline">
                Sistema de Defectos
              </Link>
            )}
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="sidebar-toggle"
              aria-label={collapsed ? 'Expandir menú' : 'Contraer menú'}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                {collapsed ? (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Menu Items */}
        <nav className="sidebar-nav">
          {menuSections.map((section) => {
            const visibleItems = section.items.filter(item => item.show);
            if (visibleItems.length === 0) return null;
            
            return (
              <div key={section.title} className="sidebar-section-group">
                {!collapsed && (
                  <div className="sidebar-section-title">{section.title}</div>
                )}
                {visibleItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`sidebar-item ${isActive(item.path) ? 'active' : ''}`}
                    title={collapsed ? item.label : ''}
                  >
                    <span className="sidebar-icon">{item.icon}</span>
                    {!collapsed && <span className="sidebar-label">{item.label}</span>}
                  </Link>
                ))}
              </div>
            );
          })}
        </nav>

        {/* Bottom Section */}
        <div className="sidebar-bottom">
          {/* Site Selector */}
          {canChangeSite() && currentSite && sites.length > 0 && (
            <div className="sidebar-section">
              {!collapsed && <label className="sidebar-section-label">Sitio</label>}
              <select
                value={currentSite.id}
                onChange={(e) => changeSite(parseInt(e.target.value))}
                className="sidebar-select"
                title={collapsed ? 'Seleccionar sitio' : ''}
              >
                {sites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {collapsed ? site.code : `${site.code} - ${site.name}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Fixed Site Display */}
          {!canChangeSite() && currentSite && (
            <div className="sidebar-section">
              {!collapsed && <label className="sidebar-section-label">Sitio</label>}
              <div className="sidebar-site-badge">
                <span className="font-bold">{currentSite.code}</span>
              </div>
            </div>
          )}

          {/* Language Toggle */}
          <button
            onClick={toggleLanguage}
            className="sidebar-item"
            title={collapsed ? (language === 'es' ? 'Cambiar a Inglés' : 'Switch to Spanish') : ''}
          >
            <span className="sidebar-icon">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
              </svg>
            </span>
            {!collapsed && <span className="sidebar-label">{language === 'es' ? 'Español' : 'English'}</span>}
          </button>

          {/* User Menu */}
          <div className="sidebar-user">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="sidebar-user-button"
            >
              <div className="sidebar-icon">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              {!collapsed && (
                <div className="flex-1 text-left min-w-0">
                  <div className="font-bold text-white text-sm truncate">{user.username}</div>
                  <div className="text-xs text-blue-200">{user.role}</div>
                </div>
              )}
              {!collapsed && (
                <svg className="w-4 h-4 text-blue-200 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </button>

            {/* User Dropdown */}
            {showUserMenu && (
              <div className={`sidebar-user-menu ${collapsed ? 'collapsed' : ''}`}>
                <Link
                  to="/about"
                  onClick={() => setShowUserMenu(false)}
                  className="sidebar-user-menu-item"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="16" x2="12" y2="12"/>
                    <circle cx="12" cy="8" r="1" fill="currentColor"/>
                  </svg>
                  <span>{t('nav.about')}</span>
                </Link>
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    setShowChangePassword(true);
                  }}
                  className="sidebar-user-menu-item"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a4 4 0 11-7.9 1M6 12l-3 3 3 3m-3-3h10a4 4 0 014 4v1" />
                  </svg>
                  <span>Cambiar contraseña</span>
                </button>
                <button
                  onClick={handleLogout}
                  className="sidebar-user-menu-item text-red-600"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H7a2 2 0 01-2-2V7a2 2 0 012-2h4a2 2 0 012 2v1"/>
                  </svg>
                  <span>{t('nav.logout')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile overlay */}
      <div className="sidebar-overlay" onClick={() => setCollapsed(true)}></div>
      <ChangePasswordModal open={showChangePassword} onClose={() => setShowChangePassword(false)} />
    </>
  );
};

export default Sidebar;
